import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { UserService } from 'src/app/shared/services/user.service';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { ReasonInputDialogComponent } from 'src/app/shared/components/reason-input-dialog/reason-input-dialog.component';
import { jsPDF } from 'jspdf';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { WordExportService } from './word-export.service';
import {
  BasicInfo,
  AgendaInfo,
  Attendee,
  QuickInfo,
  AttachmentInfo,
  AttendanceTableMember,
  AgendaDescription,
  MeetingItem,
  MeetingMinutesData,
  MinutesOfMeetingDetails,
  BadgeColor
} from 'src/app/shared/models/app.models';

@Component({
  selector: 'app-minutes-of-meeting-details',
  templateUrl: './minutes-of-meeting-details.component.html',
  styleUrls: ['./minutes-of-meeting-details.component.scss']
})
export class MinutesOfMeetingDetailsComponent implements OnInit {
  AppConstants = AppConstants;
  meetingId: string = '';
  minutesDetails: MinutesOfMeetingDetails | null = null;
  showApprovalButtons: boolean = false;
  showPublishButton: boolean = false;
  showRepublishButton: boolean = false;
  showResendEmailButton: boolean = false;
  showTCDecisionEditButtons: boolean = false;
  editingTCDecisionAgendaId: number | null = null;
  tcDecisionDraft = {
    english: '',
    arabic: ''
  };
  private tcDecisionEditorSelection: Partial<Record<'english' | 'arabic', Range>> = {};
  attendanceData: any[] = []; // Store raw attendance data with approval status
  isTranslating: boolean = false; // Flag to prevent translation loop

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _loaderService: LoaderService,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private _toasterService: ToasterService,
    private userService: UserService,
    private dialog: MatDialog,
    private wordExportService: WordExportService,
    private sanitizer: DomSanitizer,
    private translationService: TranslationService
  ) { }

  ngOnInit(): void {
    this._route.params.subscribe(params => {
      this.meetingId = params['id'];
      if (this.meetingId) {
        this.fetchMinutesDetails();
      }
    });
  }

  getTrustedDecisionHtml(html: string | null | undefined, fallback: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html || fallback);
  }

  async fetchMinutesDetails(): Promise<void> {
    this._loaderService.showLoader();

    try {
      const url = `${environment.API_URL}/api/Meetings/mom/${this.meetingId}`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));

      if (response?.isSuccess && response?.data) {
        this.attendanceData = response.data.attendance || [];
        this.minutesDetails = this.mapApiResponseToMinutesDetails(response.data);

        console.log('Fetched minutes details:', this.minutesDetails);
        // Check if user attended the meeting
        const currentUserId = this.userService.user?.Id ? parseInt(this.userService.user.Id) : null;
        const userAttended = currentUserId ? this.attendanceData.some((att: any) =>
          att.userId === currentUserId && att.attended === true
        ) : false;

        // Check if user is Secretary
        const isSecretary = this.isSecretaryRole(this.userService.user?.Role);

        const isPending = response.data.momStageName?.toLowerCase() === 'pending' || response.data.momStage === 2;
        const isDraft = !response.data.momStage || response.data.momStage === 1 ||
          response.data.momStageName?.toLowerCase() === 'draft';

        // Check if any member requested changes
        const hasChangeRequests = this.attendanceData.some((att: any) =>
          this.isChangeRequestedStatus(this.getMomApprovalStatusName(att))
        );

        // Check if current user has already provided an answer (Approved or Change Requested)
        const currentUserAnswer = this.attendanceData.find((att: any) => att.userId === currentUserId);
        const userHasAnswered = currentUserAnswer && 
          (this.getMomApprovalStatusName(currentUserAnswer).toLowerCase() === 'approved' ||
           this.isChangeRequestedStatus(this.getMomApprovalStatusName(currentUserAnswer)));

        // Show approval buttons only if:
        // - User is authenticated
        // - User attended the meeting
        // - User is NOT Secretary
        // - MOM stage is Pending
        // - User has NOT already provided an answer
        this.showApprovalButtons = this.userService.isUserAuthenticated &&
          userAttended &&
          !isSecretary &&
          isPending &&
          !userHasAnswered;

        this.showResendEmailButton = this.userService.isUserAuthenticated && isSecretary;

        // Show publish button only if:
        // - User is Secretary
        // - MOM stage is Draft (not Pending or Completed)
        this.showPublishButton = this.userService.isUserAuthenticated &&
          isSecretary &&
          isDraft;

        // Show republish button only if:
        // - User is Secretary
        // - MOM stage is Pending
        // - At least one member requested changes
        this.showRepublishButton = this.userService.isUserAuthenticated &&
          isSecretary &&
          isPending &&
          hasChangeRequests;

        this.showTCDecisionEditButtons = this.userService.isUserAuthenticated &&
          isSecretary &&
          hasChangeRequests;

        this.cdr.detectChanges();
      } else {
        console.error('Failed to fetch minutes details:', response?.errorMessageEn);
        this.minutesDetails = null;
        this.showResendEmailButton = false;
        this.cdr.detectChanges();
      }
    } catch (error) {
      console.error('Failed to fetch minutes details:', error);
      this.minutesDetails = null;
      this.cdr.detectChanges();
    } finally {
      this._loaderService.hideLoader();
    }
  }

  mapApiResponseToMinutesDetails(apiData: any): MinutesOfMeetingDetails {
    const dateTime = new Date(apiData.basicInfo.dateTime);
    const meetingDate = dateTime.toISOString().split('T')[0];
    const meetingTime = dateTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    const createdDate = apiData.basicInfo.createdAt ? new Date(apiData.basicInfo.createdAt).toISOString().split('T')[0] : '';

    // Map attendance data
    const attendanceTable: AttendanceTableMember[] = apiData.attendance.map((att: any, index: number) => ({
      number: index + 1,
      memberNameArabic: att.userNameAr || '',
      memberNameEnglish: att.userName || '',
      roleArabic: att.role || '',
      roleEnglish: att.role || '',
      attendance: att.attended
    }));
    
    // Sort attendance table by role: Chairman -> Vice Chairman -> Secretary -> TCM -> Observer
    attendanceTable.sort((a, b) => {
      const roleOrder: { [key: string]: number } = {
        'chairman': 1,
        'vicechairman': 2,
        'vice chairman': 2,
        'secretary': 3,
        'sec': 3,
        'tcm': 4,
        'member': 4,
        'observer': 5
      };
      
      const roleA = (a.roleEnglish || '').toLowerCase().replace(/\s+/g, '');
      const roleB = (b.roleEnglish || '').toLowerCase().replace(/\s+/g, '');
      
      const orderA = roleOrder[roleA] || 4; // Default to TCM if role not found
      const orderB = roleOrder[roleB] || 4;
      
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      
      // If same role, sort alphabetically by name
      return (a.memberNameEnglish || '').localeCompare(b.memberNameEnglish || '');
    });
    
    // Update numbers after sorting
    attendanceTable.forEach((member, index) => {
      member.number = index + 1;
    });

    // Map agenda decisions to meeting items
    // Sort by Order property (ascending - Item #1 first, Item #2 second, etc.)
    const items: MeetingItem[] = apiData.agendaDecisions
      .slice() // Create a copy to avoid mutating original
      .sort((a: any, b: any) => {
        const orderA = a.order || a.Order || 999;
        const orderB = b.order || b.Order || 999;
        return orderA - orderB;
      })
      .map((decision: any, index: number) => ({
        agendaId: decision.agendaId || decision.AgendaId,
        itemNumber: (decision.order || decision.Order || (index + 1)).toString(),
        company: decision.subject || '',
        subject: decision.subject || '',
        englishText: 'The Tender Committee reviewed the attached documents.',
        arabicText: 'اطلعت اللجنة على المستندات المرفقة',
        decisionEnglish: decision.tcDecisionEN || '',
        decisionArabic: decision.tcDecisionAR || '',
        acceptedVotes: decision.acceptedVotes || 0,
        rejectedVotes: decision.rejectedVotes || 0,
        deferredVotes: decision.deferredVotes || 0,
        totalVotes: (decision.acceptedVotes || 0) + (decision.rejectedVotes || 0) + (decision.deferredVotes || 0)
      }));

    // Get first agenda item for agenda info (if available)
    const firstAgenda = apiData.agendaDecisions.length > 0 ? apiData.agendaDecisions[0] : null;

    return {
      basicInfo: {
        subject: apiData.basicInfo.meetingTitle || '',
        meetingId: apiData.basicInfo.meetingId || '',
        department: '', // Removed as per user request
        createdDate: createdDate,
        meetingDate: meetingDate,
        meetingTime: meetingTime,
        location: apiData.basicInfo.location || ''
      },
      agendaInfo: {
        titleEnglish: firstAgenda?.agendaTitleEN || '',
        titleArabic: firstAgenda?.agendaTitleAR || ''
      },
      attendees: apiData.attendance
        .filter((att: any) => att.attended)
        .map((att: any) => ({ name: att.userName || '' })),
      discussionPoints: '', // Not available from API
      decisionsMade: '', // Not available from API
      actionItems: '', // Not available from API
      quickInfo: {
        meetingDate: `${meetingDate} ${meetingTime}`,
        department: '', // Removed as per user request
        currentStage: apiData.momStageName || 'Draft'
      },
      attachment: {
        fileName: '',
        fileSize: ''
      },
      meetingMinutes: {
        recordedBy: '',
        recordedDate: createdDate,
        summary: '',
        attendanceTable: attendanceTable,
        agendaDescription: {
          english: '',
          arabic: ''
        },
        items: items
      }
    };
  }


  goBack(): void {
    this._router.navigate([`/${AppConstants.ROUTES.MINUTES_OF_MEETING}`]);
  }

  async resendApprovalEmails(): Promise<void> {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '520px',
      data: {
        title: 'Resend Approval Emails',
        message: 'Are you sure you want to resend the approval request emails to all attendees? Their current approval status will not be changed.',
        confirmText: 'Yes, Resend',
        cancelText: 'Cancel'
      }
    });

    const result = await lastValueFrom(dialogRef.afterClosed());
    if (!result) {
      return;
    }

    this._loaderService.showLoader();

    try {
      const url = `${environment.API_URL}/api/Meetings/mom/${this.meetingId}/resend-emails`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, {}));

      if (response?.isSuccess) {
        this._toasterService.showSuccess('Approval request emails resent successfully');
        await this.fetchMinutesDetails();
      } else {
        this._toasterService.showError(response?.errorMessageEn || 'Failed to resend approval request emails');
      }
    } catch (error: any) {
      console.error('Failed to resend approval request emails:', error);
      this._toasterService.showError(error?.error?.errorMessageEn || 'Failed to resend approval request emails');
    } finally {
      this._loaderService.hideLoader();
    }
  }


  async exportWord(): Promise<void> {
    if (!this.minutesDetails) {
      this._toasterService.showError('No meeting details available to export');
      return;
    }

    this._loaderService.showLoader();

    try {
      await this.wordExportService.exportWord(this.minutesDetails);
      this._loaderService.hideLoader();
      this._toasterService.showSuccess('Word document exported successfully');
    } catch (error: any) {
      console.error('Error during Word export:', error);
      this._loaderService.hideLoader();
      this._toasterService.showError(error?.message || 'An error occurred while exporting Word document');
    }
  }


  viewDocument(): void {
    if (this.minutesDetails?.attachment?.url) {
      window.open(this.minutesDetails.attachment.url, '_blank');
    } else {
      console.log('View document:', this.minutesDetails?.attachment?.fileName);
      // TODO: Implement document viewing logic
    }
  }

  getStatusColor(status: string): BadgeColor {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'green';
      case 'pending':
        return 'yellow';
      case 'draft':
        return 'gray';
      default:
        return 'gray';
    }
  }

  trackByAttendee(index: number, attendee: Attendee): string {
    return attendee.name;
  }

  trackByAttendanceMember(index: number, member: AttendanceTableMember): number {
    return member.number;
  }

  trackByMeetingItem(index: number, item: MeetingItem): string {
    return item.itemNumber;
  }

  startEditTCDecision(item: MeetingItem): void {
    this.editingTCDecisionAgendaId = item.agendaId;
    this.tcDecisionDraft = {
      english: item.decisionEnglish || '',
      arabic: item.decisionArabic || ''
    };
    this.tcDecisionEditorSelection = {};
    setTimeout(() => {
      this.setTCDecisionEditorContent('english', this.tcDecisionDraft.english);
      this.setTCDecisionEditorContent('arabic', this.tcDecisionDraft.arabic);
      this.cdr.detectChanges();
    });
  }

  cancelEditTCDecision(): void {
    this.editingTCDecisionAgendaId = null;
    this.tcDecisionDraft = {
      english: '',
      arabic: ''
    };
    this.tcDecisionEditorSelection = {};
  }

  async saveTCDecision(item: MeetingItem, englishEditor: HTMLDivElement, arabicEditor: HTMLDivElement): Promise<void> {
    if (!item.agendaId) {
      this._toasterService.showError('Agenda item ID was not found.');
      return;
    }

    const tcDecisionEN = this.normalizeEditorHtml(englishEditor.innerHTML);
    const tcDecisionAR = this.normalizeEditorHtml(arabicEditor.innerHTML);

    this._loaderService.showLoader();

    try {
      const url = `${environment.API_URL}/api/AgendaItems/${item.agendaId}/tc-decision`;
      const payload = {
        meetingId: this.meetingId,
        tcDecisionEN,
        tcDecisionAR
      };
      const response: any = await lastValueFrom(this.apiService.putByUrl(url, payload));

      if (response?.isSuccess) {
        item.decisionEnglish = tcDecisionEN;
        item.decisionArabic = tcDecisionAR;
        this._toasterService.showSuccess('TC Decision updated successfully');
        this.cancelEditTCDecision();
        await this.fetchMinutesDetails();
      } else {
        this._toasterService.showError(response?.errorMessageEn || 'Failed to update TC Decision');
      }
    } catch (error: any) {
      console.error('Failed to update TC Decision:', error);
      this._toasterService.showError(error?.error?.errorMessageEn || 'Failed to update TC Decision');
    } finally {
      this._loaderService.hideLoader();
    }
  }

  executeEditorCommand(
    editor: HTMLDivElement,
    language: 'english' | 'arabic',
    command: string,
    value?: string
  ): void {
    editor.focus();
    this.restoreEditorSelection(language, editor);
    if (command === 'foreColor' || command === 'hiliteColor' || command === 'backColor') {
      document.execCommand('styleWithCSS', false, 'true');
    }
    const commandApplied = document.execCommand(command, false, value || '');
    if (!commandApplied && command === 'hiliteColor') {
      document.execCommand('backColor', false, value || '');
    }
    this.updateTCDecisionDraft(language, editor);
    this.saveEditorSelection(language, editor);
  }

  preserveEditorSelection(event: MouseEvent, language: 'english' | 'arabic', editor: HTMLDivElement): void {
    this.saveEditorSelection(language, editor);
    const target = event.target as HTMLInputElement | null;
    if (target?.tagName?.toLowerCase() === 'input' && target.type === 'color') {
      return;
    }

    event.preventDefault();
  }

  onTCDecisionDraftInput(language: 'english' | 'arabic', editor: HTMLDivElement): void {
    this.updateTCDecisionDraft(language, editor);
    this.saveEditorSelection(language, editor);
  }

  onTCDecisionEditorPaste(event: ClipboardEvent, language: 'english' | 'arabic', editor: HTMLDivElement): void {
    const pastedText = event.clipboardData?.getData('text/plain');

    if (!pastedText) {
      return;
    }

    event.preventDefault();
    editor.focus();
    this.restoreEditorSelection(language, editor);
    document.execCommand('insertText', false, this.normalizePastedPlainText(pastedText));
    this.updateTCDecisionDraft(language, editor);
    this.saveEditorSelection(language, editor);
  }

  onTCDecisionEditorBlur(language: 'english' | 'arabic'): void {
    // Optional: could add validation logic here
  }

  hasTCDecisionDraftText(language: 'english' | 'arabic'): boolean {
    return this.getPlainTextFromHtml(this.tcDecisionDraft[language]).length > 0;
  }

  isTCDecisionDraftEmpty(language: 'english' | 'arabic'): boolean {
    return !this.hasTCDecisionDraftText(language);
  }

  async onTranslateToArabicMom(arabicEditor: HTMLDivElement): Promise<void> {
    const englishHtml = this.tcDecisionDraft.english || '';
    const englishText = this.getPlainTextFromHtml(englishHtml);

    if (!englishText || englishText.trim() === '') {
      this._toasterService.showWarning('Please enter text in English to translate', 'No Text');
      return;
    }

    this.isTranslating = true;
    try {
      const translatedHtml = await this.translateEditorHtml(englishHtml);
      this.tcDecisionDraft.arabic = translatedHtml;
      arabicEditor.innerHTML = translatedHtml;
      this._toasterService.showSuccess('Text translated successfully', 'Translation Complete');
    } catch (error) {
      console.error('Translation failed:', error);
      this._toasterService.showError('Translation failed. Please try again.', 'Translation Error');
    } finally {
      this.isTranslating = false;
    }
  }

  private updateTCDecisionDraft(language: 'english' | 'arabic', editor: HTMLDivElement): void {
    const normalizedHtml = this.normalizeEditorHtml(editor.innerHTML);
    this.tcDecisionDraft[language] = normalizedHtml;
  }

  saveEditorSelection(language: 'english' | 'arabic', editor: HTMLDivElement): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) {
      return;
    }

    this.tcDecisionEditorSelection[language] = range.cloneRange();
  }

  private restoreEditorSelection(language: 'english' | 'arabic', editor: HTMLDivElement): void {
    const range = this.tcDecisionEditorSelection[language];
    if (!range || !editor.contains(range.commonAncestorContainer)) {
      return;
    }

    const selection = window.getSelection();
    if (!selection) {
      return;
    }

    selection.removeAllRanges();
    selection.addRange(range);
  }

  private setTCDecisionEditorContent(language: 'english' | 'arabic', html: string): void {
    const editor = document.querySelector<HTMLDivElement>(`[data-tc-decision-editor="${language}"]`);
    if (editor && editor.innerHTML !== html) {
      editor.innerHTML = html;
    }
  }

  private normalizePastedPlainText(text: string): string {
    return text
      .replace(/\r\n?/g, '\n')
      .replace(/ /g, ' ')
      .replace(/\t/g, ' ')
      .split('\n')
      .map(line => line.replace(/[ ]{2,}/g, ' ').trimEnd())
      .join('\n')
      .trim();
  }

  private async translateEditorHtml(html: string): Promise<string> {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    const textNodes: Text[] = [];
    const walker = document.createTreeWalker(wrapper, NodeFilter.SHOW_TEXT);
    let currentNode = walker.nextNode();

    while (currentNode) {
      const textNode = currentNode as Text;
      if (textNode.textContent && textNode.textContent.trim()) {
        textNodes.push(textNode);
      }
      currentNode = walker.nextNode();
    }

    for (const textNode of textNodes) {
      textNode.textContent = await this.translateEditorTextNode(textNode.textContent || '');
    }

    return this.normalizeEditorHtml(wrapper.innerHTML);
  }

  private async translateEditorTextNode(text: string): Promise<string> {
    const parts = text.split(/(\n+)/);
    const translatedParts: string[] = [];

    for (const part of parts) {
      if (!part.trim()) {
        translatedParts.push(part);
        continue;
      }

      const leadingWhitespace = part.match(/^\s*/)?.[0] || '';
      const trailingWhitespace = part.match(/\s*$/)?.[0] || '';
      const translatedText = this.cleanTranslatedText(
        await lastValueFrom(this.translationService.translateToArabic(part.trim()))
      );

      translatedParts.push(`${leadingWhitespace}${translatedText}${trailingWhitespace}`);
    }

    return translatedParts.join('');
  }

  private cleanTranslatedText(text: string): string {
    const decoder = document.createElement('textarea');
    decoder.innerHTML = text || '';
    return decoder.value
      .replace(/<\/?g\b[^>]*>/gi, '')
      .replace(/<\/?x\b[^>]*>/gi, '')
      .replace(/<[^>]+>/g, '')
      .trim();
  }

  private normalizeEditorHtml(html: string): string {
    return this.getPlainTextFromHtml(html) ? html : '';
  }

  private getPlainTextFromHtml(html: string): string {
    const element = document.createElement('div');
    element.innerHTML = html || '';
    return (element.textContent || element.innerText || '')
      .replace(/\u00a0/g, ' ')
      .trim();
  }

  /**
   * Check if the current user has already provided their answer (Approved or Change Requested)
   * @returns true if user has already answered, false otherwise
   */
  hasCurrentUserAnswered(): boolean {
    const currentUserId = this.userService.user?.Id ? parseInt(this.userService.user.Id) : null;
    if (!currentUserId) return false;

    const currentUserAnswer = this.attendanceData.find((att: any) => att.userId === currentUserId);
    return currentUserAnswer && 
      (this.getMomApprovalStatusName(currentUserAnswer).toLowerCase() === 'approved' ||
       this.isChangeRequestedStatus(this.getMomApprovalStatusName(currentUserAnswer)));
  }

  getAttendedMembers(): any[] {
    // Filter out Secretary users from the approval status table
    const filtered = this.attendanceData.filter((att: any) => {
      if (!att.attended) return false;
      // Exclude Secretary users
      const roleLower = (att.role || '').toLowerCase();
      return roleLower !== 'secretary' && roleLower !== 'sec';
    });
    
    // Sort by role: Chairman -> Vice Chairman -> TCM -> Observer
    const roleOrder: { [key: string]: number } = {
      'chairman': 1,
      'vicechairman': 2,
      'vice chairman': 2,
      'tcm': 3,
      'member': 3,
      'observer': 4
    };
    
    return filtered.sort((a, b) => {
      const roleA = (a.role || '').toLowerCase().replace(/\s+/g, '');
      const roleB = (b.role || '').toLowerCase().replace(/\s+/g, '');
      
      const orderA = roleOrder[roleA] || 3; // Default to TCM if role not found
      const orderB = roleOrder[roleB] || 3;
      
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      
      // If same role, sort alphabetically by name
      const nameA = (a.userName || a.userNameAr || '').toLowerCase();
      const nameB = (b.userName || b.userNameAr || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }

  trackByMember(index: number, member: any): number {
    return member.userId;
  }

  getApprovalBadgeClass(status: string | null | undefined): string {
    if (!status) return 'approval-badge-pending';
    const statusLower = status.toLowerCase();
    if (statusLower === 'approved') return 'approval-badge-approved';
    if (this.isChangeRequestedStatus(status)) return 'approval-badge-change-requested';
    return 'approval-badge-pending';
  }

  isChangeRequestedStatus(status: string | null | undefined): boolean {
    return (status || '').toLowerCase().replace(/\s+/g, '') === 'changerequested';
  }

  getMomApprovalStatusName(member: any): string {
    return (member?.momApprovalStatusName ||
      member?.MOMApprovalStatusName ||
      member?.momApprovalStatus ||
      member?.MomApprovalStatus ||
      member?.approvalStatusName ||
      member?.ApprovalStatusName ||
      '').toString();
  }

  private isSecretaryRole(role: any): boolean {
    const roleValue = typeof role === 'string' && !isNaN(Number(role)) ? Number(role) : role;
    const normalizedRole = (role || '').toString().trim().toLowerCase().replace(/\s+/g, '');

    return normalizedRole === 'secretary' ||
      normalizedRole === 'sec' ||
      normalizedRole === 'tendercommitteesecretary' ||
      roleValue === 4;
  }

  async publishMOM(): Promise<void> {
    if (!this.minutesDetails) return;

    // Show confirmation dialog
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Publish Minutes of Meeting',
        message: 'Are you sure you want to publish this Minutes of Meeting? This will send approval requests to all attendees.',
        confirmText: 'Yes, Publish',
        cancelText: 'Cancel'
      }
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (!result) {
      return;
    }

    this._loaderService.showLoader();

    try {
      const url = `${environment.API_URL}/api/Meetings/mom/${this.meetingId}/stage`;
      const payload = { momStage: 2 }; // 2 = Pending
      const response: any = await lastValueFrom(this.apiService.putByUrl(url, payload));

      if (response?.isSuccess) {
        this._toasterService.showSuccess('MOM published successfully');
        // Refresh the data to get updated MOM Stage
        await this.fetchMinutesDetails();
      } else {
        this._toasterService.showError(response?.errorMessageEn || 'Failed to publish MOM');
      }
    } catch (error: any) {
      console.error('Failed to publish MOM:', error);
      this._toasterService.showError(error?.error?.errorMessageEn || 'Failed to publish MOM');
    } finally {
      this._loaderService.hideLoader();
    }
  }

  async approveMOM(): Promise<void> {
    if (!this.userService.user?.Id) {
      this._toasterService.showError('User not authenticated');
      return;
    }

    // Show confirmation dialog
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Approve Minutes of Meeting',
        message: 'Are you sure you want to approve this Minutes of Meeting?',
        confirmText: 'Yes, Approve',
        cancelText: 'Cancel'
      }
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (!result) {
      return;
    }

    this._loaderService.showLoader();

    try {
      const url = `${environment.API_URL}/api/Meetings/mom/${this.meetingId}/approval`;
      const payload = {
        status: 'Approved'
      };
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, payload));

      if (response?.isSuccess) {
        this._toasterService.showSuccess('MOM approved successfully');
        // Refresh data to get updated approval status and MOM stage
        await this.fetchMinutesDetails();
      } else {
        this._toasterService.showError(response?.errorMessageEn || 'Failed to approve MOM');
      }
    } catch (error: any) {
      console.error('Failed to approve MOM:', error);
      this._toasterService.showError(error?.error?.errorMessageEn || 'Failed to approve MOM');
    } finally {
      this._loaderService.hideLoader();
    }
  }

  async requestChangeMOM(): Promise<void> {
    if (!this.userService.user?.Id) {
      this._toasterService.showError('User not authenticated');
      return;
    }

    // Show reason input dialog
    const dialogRef = this.dialog.open(ReasonInputDialogComponent, {
      width: '500px',
      data: {
        title: 'Request Changes to MOM',
        message: 'Please provide a reason for requesting changes to the Minutes of Meeting:',
        confirmText: 'Submit Request',
        cancelText: 'Cancel'
      }
    });

    const reason = await dialogRef.afterClosed().toPromise();
    if (!reason) {
      return;
    }

    this._loaderService.showLoader();

    try {
      const url = `${environment.API_URL}/api/Meetings/mom/${this.meetingId}/approval`;
      const payload = {
        status: 'ChangeRequested',
        reason: reason
      };
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, payload));

      if (response?.isSuccess) {
        this._toasterService.showSuccess('Change request submitted successfully');
        // Refresh data to get updated approval status
        await this.fetchMinutesDetails();
      } else {
        this._toasterService.showError(response?.errorMessageEn || 'Failed to submit change request');
      }
    } catch (error: any) {
      console.error('Failed to submit change request:', error);
      this._toasterService.showError(error?.error?.errorMessageEn || 'Failed to submit change request');
    } finally {
      this._loaderService.hideLoader();
    }
  }

  async republishMOM(): Promise<void> {
    if (!this.minutesDetails) return;

    // Show confirmation dialog
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Re-publish Minutes of Meeting',
        message: 'Are you sure you want to re-publish this MOM? This will reset all approval statuses to Pending and send new approval requests to all attendees.',
        confirmText: 'Yes, Re-publish',
        cancelText: 'Cancel'
      }
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (!result) {
      return;
    }

    this._loaderService.showLoader();

    try {
      const url = `${environment.API_URL}/api/Meetings/mom/${this.meetingId}/republish`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, {}));

      if (response?.isSuccess) {
        this._toasterService.showSuccess('MOM re-published successfully. All approval statuses have been reset to Pending.');
        // Refresh the data to get updated MOM Stage and approval statuses
        await this.fetchMinutesDetails();
      } else {
        this._toasterService.showError(response?.errorMessageEn || 'Failed to re-publish MOM');
      }
    } catch (error: any) {
      console.error('Failed to re-publish MOM:', error);
      this._toasterService.showError(error?.error?.errorMessageEn || 'Failed to re-publish MOM');
    } finally {
      this._loaderService.hideLoader();
    }
  }
}
